import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import SectionCard from '@Guidelines/Component/SectionCard';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import WordingInput from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Wording/WordingInput';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Checkbox, type CheckboxValue, Message } from '@verclam/ui-kit';

const USER_ABOUT = 'user';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isReadOnly: boolean;
};

/** The populations a dossier of this type may be about, with a warning when users are among them. */
const AboutSection: React.FC<Props> = ({ formData, onChange, isReadOnly }) => {
	const abouts = useSelector((state) => state.subjectTypeDefinitionsStore.abouts);
	const serverMessage = formData.getAboutServerMessage();

	const handleToggle = React.useCallback(
		(checked: boolean, value: CheckboxValue | undefined) => {
			if (typeof value !== 'string') {
				return;
			}

			onChange((current) =>
				current.setAbout(
					checked ? [...current.about, value] : current.about.filter((about) => about !== value),
				),
			);
		},
		[onChange],
	);

	return (
		<SectionCard
			title="Porte sur"
			description="Les dossiers existants gardent leur lien ; le réglage vaut pour les créations."
		>
			{(abouts ?? []).map((about) => (
				<Checkbox
					key={about.value}
					name="about"
					label={about.label}
					value={about.value}
					checked={formData.about.includes(about.value)}
					disabled={isReadOnly}
					onChange={handleToggle}
				/>
			))}
			<ConditionalLoader data={formData.about.includes(USER_ABOUT)}>
				<Message
					severity="info"
					text="Un dossier qui porte sur un utilisateur lui reste invisible : ne cochez pas « Utilisateur » pour des notes personnelles."
				/>
			</ConditionalLoader>
			<ConditionalLoader data={formData.about.length > 0}>
				<WordingInput
					formData={formData}
					onChange={onChange}
					part="aboutLabel"
					label="Libellé du champ « Porte sur »"
					subLabel="Exemple : Salarié évalué."
					required
					disabled={isReadOnly}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={serverMessage}>
				{(message) => <small className="ui-kit-field-error">{message}</small>}
			</ConditionalLoader>
		</SectionCard>
	);
};

export default AboutSection;
